import { ActionIcon, CloseButton, Loader, Menu, UnstyledButton } from "@mantine/core";
import {
  AudioLines,
  ImageOff,
  LibraryBig,
  Pause,
  Play,
  Plus,
  Settings2,
  SmilePlus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Making } from "../../../../platform/actors/use-makings";
import { failedWords } from "../../../../platform/jobs/failed-words";
import { playsAlone, stoppedPlaying } from "../../../../ui/AudioPlayer";
import { voiceOf, type Cast, type TakeState } from "./cast";
import classes from "./talking-head.module.css";

export interface CastRowProps {
  casts: readonly Cast[];
  /** Each actor's voiceover, in the casts' order. */
  takes: readonly TakeState[];
  /** A voice's sample, once the voices are read; none before. */
  sampleOf: (voiceId: string) => string | null;
  /** Live's four actors: no "+ AI Actor" once they are there. */
  full: boolean;
  /** While the videos start, nothing changes, as live's. */
  disabled: boolean;
  /** Speech to speech: plain cards, the one speech is every actor's (live's `AvatarsList`). */
  voiceless: boolean;
  /** The actors "Add AI Actor" is making, after the cast, as live's pending cards. */
  makings: readonly Making[];
  onRemove: (actorId: string) => void;
  onVoice: (actorId: string) => void;
  onFromLibrary: () => void;
  /** Live's "Add AI Actor"; none while no model makes actors. */
  onMakeActor: (() => void) | null;
  /** Takes a failed pending card out. */
  onDropMaking: (jobId: string) => void;
  /**
   * In a page's card (the Pro Studio redesign's), not the Studio's panel at the window's foot: the
   * tile's menu opens below it, into the page, rather than over what sits above the card.
   */
  inline?: boolean | undefined;
}

/**
 * Live's actors row (`avatars-section`, captures 07 and L09b to L09f): each actor's card, then
 * each actor "Add AI Actor" is making (live's `generation-avatar-card`), then the "+ AI Actor"
 * tile while there is room. The tile opens live's menu: "From Library" opens Add Actors; "Add AI
 * Actor" opens the actor composer in the form's place (issue 238).
 */
export function CastRow(props: CastRowProps) {
  const { casts, takes, sampleOf, full, disabled, voiceless, makings } = props;
  const { onRemove, onVoice, onFromLibrary, onMakeActor, onDropMaking } = props;
  return (
    <fieldset className={classes.cast} aria-label="AI Actors">
      {casts.map((cast, at) => (
        <CastCard
          key={cast.actor.key}
          cast={cast}
          take={takes[at] ?? { state: "none" }}
          sample={(() => {
            const voice = voiceOf(cast);
            return voice === null ? null : (cast.picked?.preview_url ?? sampleOf(voice.id));
          })()}
          disabled={disabled}
          voiceless={voiceless}
          onRemove={() => onRemove(cast.actor.key)}
          onVoice={() => onVoice(cast.actor.key)}
        />
      ))}
      {makings.map((making) => (
        <MakingCard
          key={making.jobId}
          making={making}
          disabled={disabled}
          onRemove={() => onDropMaking(making.jobId)}
        />
      ))}
      {full ? null : (
        <Menu position={props.inline === true ? "bottom-start" : "top-start"} disabled={disabled}>
          <Menu.Target>
            <UnstyledButton className={classes.addTile} aria-label="AI Actor" disabled={disabled}>
              <Plus size={20} strokeWidth={1.5} aria-hidden />
              <span>AI Actor</span>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown className={classes.menu}>
            <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={onFromLibrary}>
              From Library
            </Menu.Item>
            <Menu.Item
              leftSection={<SmilePlus size={16} strokeWidth={1.5} />}
              disabled={onMakeActor === null}
              onClick={onMakeActor ?? undefined}
            >
              Add AI Actor
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </fieldset>
  );
}

/**
 * An actor "Add AI Actor" is making (live's `generation-avatar-card`): a loader while its job
 * runs; once it failed, live's words on a failed image and its x, which only then shows.
 */
function MakingCard(props: { making: Making; disabled: boolean; onRemove: () => void }) {
  const { making, disabled, onRemove } = props;
  const words = failedWords("image.actor", undefined);
  return (
    <fieldset className={classes.card} aria-label="New AI Actor">
      {making.failed ? (
        <div className={classes.makingFailed} role="alert">
          <ImageOff size={24} aria-hidden />
          <span className={classes.makingTitle}>{words.title}</span>
          <span className={classes.makingText}>{words.description}</span>
        </div>
      ) : (
        <div className={classes.making}>
          <Loader size={24} aria-label="Making the AI Actor" />
        </div>
      )}
      {disabled || !making.failed ? null : (
        <CloseButton
          size="sm"
          className={classes.remove}
          aria-label="Remove the failed AI Actor"
          onClick={onRemove}
        />
      )}
    </fieldset>
  );
}

interface CastCardProps {
  cast: Cast;
  take: TakeState;
  sample: string | null;
  disabled: boolean;
  voiceless: boolean;
  onRemove: () => void;
  onVoice: () => void;
}

/**
 * An actor's card (`voice-avatar-card`, L09b to L09f): the portrait, live's x at the corner on
 * hover, and its voice: a loader while its voiceover is made; "+ Add Voice" without one; else
 * the voice (or, once made, its voiceover, live's "Audio") to play, and the gear that opens
 * Voice Settings.
 */
function CastCard({ cast, take, sample, disabled, voiceless, onRemove, onVoice }: CastCardProps) {
  const voice = voiceOf(cast);
  const { actor } = cast;
  let slot;
  if (voiceless) {
    slot = null;
  } else if (take.state === "running") {
    slot = <Loader size={24} className={classes.voiceLoader} aria-label={`Making ${actor.name}'s audio`} />;
  } else if (voice === null) {
    slot = (
      <UnstyledButton className={classes.addVoice} disabled={disabled} onClick={onVoice}>
        <span className={classes.voiceIcon} aria-hidden>
          <Plus size={16} />
        </span>
        <span className={classes.voiceName}>Add Voice</span>
      </UnstyledButton>
    );
  } else {
    const made = take.state === "done";
    slot = (
      <div className={classes.voice}>
        <PlayChip
          url={made ? take.url : sample}
          name={made ? "Audio" : voice.name}
          wave={!made}
          owner={actor.name}
        />
        <ActionIcon
          variant="transparent"
          size={24}
          className={classes.gear}
          disabled={disabled}
          aria-label={`Voice Settings: ${actor.name}`}
          onClick={onVoice}
        >
          <Settings2 size={18} />
        </ActionIcon>
      </div>
    );
  }
  return (
    <fieldset className={classes.card} aria-label={actor.name}>
      {actor.imageUrl === null ? (
        <span className={classes.portrait} aria-hidden />
      ) : (
        <img className={classes.portrait} src={actor.imageUrl} alt="" />
      )}
      {slot}
      {disabled ? null : (
        <CloseButton
          size="sm"
          className={classes.remove}
          aria-label={`Remove ${actor.name}`}
          onClick={onRemove}
        />
      )}
    </fieldset>
  );
}

interface PlayChipProps {
  /** The sample or the voiceover; none until the voices are read. */
  url: string | null;
  /** The voice's name, or live's "Audio" for a voiceover. */
  name: string;
  /** Live's waveform for a voice; the play button for a voiceover (`audio-slot`). */
  wave: boolean;
  /** The actor, to tell one card's button from another's. */
  owner: string;
}

/** Live's small player on the card: its round button and the name, cut to the card. */
function PlayChip({ url, name, wave, owner }: PlayChipProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const element = audio.current;
    return () => element?.pause();
  }, []);
  let icon = <Play size={14} />;
  if (playing) icon = <Pause size={14} />;
  else if (wave) icon = <AudioLines size={16} />;
  return (
    <>
      <ActionIcon
        variant="light"
        size={24}
        radius="xl"
        className={classes.play}
        data-playing={playing || undefined}
        disabled={url === null}
        aria-label={`${playing ? "Pause" : "Play"} ${owner}'s ${wave ? "voice" : "audio"}`}
        onClick={() => {
          const element = audio.current;
          if (element === null) return;
          if (element.paused) void element.play().catch(() => setPlaying(false));
          else element.pause();
        }}
      >
        {icon}
      </ActionIcon>
      <span className={classes.voiceName} title={name}>
        {name}
      </span>
      {url === null ? null : (
        // A voice sample has no captions to give.
        // oxlint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audio}
          src={url}
          preload="none"
          onPlay={(event) => {
            playsAlone(event.currentTarget);
            setPlaying(true);
          }}
          onPause={(event) => {
            stoppedPlaying(event.currentTarget);
            setPlaying(false);
          }}
          onEnded={(event) => {
            event.currentTarget.currentTime = 0;
          }}
        />
      )}
    </>
  );
}
